// The download page's shared pieces. /download is the download zone, The Gate (zones/download/, ZONES_AT_SLUG in
// main.ts); it uses these: the `?for=` action, the phone's primary action, the Windows waiting list, the requirements
// rows and the FAQ answers. Every button there comes from /downloads/latest.txt (downloads.ts); never a date.
import { isComingSoon, type Game } from "../catalogue.ts";
import { api } from "../api.ts";
import { clear, errorBox, h, link, toast } from "../dom.ts";
import { icon } from "../icons.ts";
import { gameActionLink, launch, playLink, type GameAction } from "../play.ts";

/** What `?for=` asks the app to do. Tips only while selling (wave 9); a coming-soon game means its waitlist. */
export function downloadAction(forParam: string | null, comingSoon: boolean, isOn: boolean): GameAction | null {
  if (forParam === "tip" && isOn) return "tip";
  return forParam === "notify" || comingSoon ? "notify" : null;
}

/** The FAQ's "How does paying work?" answer (only while selling). */
export function balanceAnswer(): string {
  return "In the Banger app you top up your balance from $5 (or €5) with a card through Stripe, then spend it on items and tips. Prices include tax. Unspent top-ups can be refunded within 14 days. Your balance stays in Banger: it can't be cashed out or sent to someone else.";
}

/** The FAQ's "Is Banger free?" answer: no prices are mentioned while nothing is sold. */
export function freeAnswer(isOn: boolean): string {
  return isOn
    ? "Yes. The app and every game on it are free. Some games sell extras: you top up a Banger balance in dollars or euros and see each price before you buy."
    : "Yes. The app is free to download and use, and so is every game on it.";
}

/**
 * A phone's primary action: play in the browser (the game's own site, or the games on this site), plus a way to get
 * this page onto a computer. Nothing is sent anywhere: the share sheet or the clipboard, the visitor's choice.
 */
export function phonePrimary(game?: Game): HTMLElement {
  const web = game && !isComingSoon(game) && game.runtime === "web" && /^https:\/\//.test(game.url) ? game.url : null;
  const play = web
    ? h("a", { class: "btn btn-big", href: web, target: "_blank", rel: "noopener" }, icon("play", 20), `Play ${game!.title} in browser`)
    : link("/", { class: "btn btn-big" }, icon("play", 20), "Play in browser");
  return h("div", { class: "dl-primary" }, play, sendToDesktop());
}

/** "Send it to my desktop": the page's link through the phone's share sheet, else copied. */
export function sendToDesktop(): HTMLElement {
  const url = `${location.origin}/download`;
  return h(
    "p",
    { class: "dim small dl-send" },
    "Want the app? ",
    h(
      "button",
      {
        type: "button",
        class: "dl-send-btn",
        style: { background: "none", border: "0", padding: "0", font: "inherit", color: "var(--text)", textDecoration: "underline", cursor: "pointer" },
        onclick: async () => {
          const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
          try {
            if (nav.share) await nav.share({ title: "Get Banger", url });
            else {
              await navigator.clipboard.writeText(url);
              toast("Link copied: open it on your computer");
            }
          } catch {
            /* the share sheet was closed */
          }
        },
      },
      "Send this page to your computer",
    ),
    " and download it there.",
  );
}


export function alreadyInstalled(game: Game, action: GameAction | null): HTMLElement {
  const href = action ? gameActionLink(game.id, action) : playLink(game.id);
  const text = action === "tip" ? "Tip in Banger" : action === "notify" ? "Join the waitlist in Banger" : `Open ${game.title} in Banger`;
  return h(
    "p",
    { class: "small" },
    "Already installed? ",
    h(
      "a",
      {
        href,
        onclick: (e: Event) => {
          e.preventDefault();
          launch(game.id, { link: href });
        },
      },
      text,
    ),
    " · ",
    link(`/g/${game.id}`, null, "Back to the game"),
  );
}

/** The Windows app's waiting list: one email field (POST /v1/app-waitlist), no account. */
export function waitlistForm(idSuffix = ""): HTMLElement {
  const id = `dl-waitlist-email${idSuffix}`;
  const msg = h("div", { role: "status" });
  const form: HTMLFormElement = h(
    "form",
    {
      class: "dl-waitlist",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        const email = String(new FormData(form).get("email") ?? "").trim();
        try {
          await api("accounts", "/app-waitlist", { body: { email, platform: "windows" }, auth: false });
          clear(form, h("p", null, "You're on the list. We'll email ", h("strong", null, email), " when Banger for Windows is out."));
        } catch (err) {
          clear(msg, errorBox(err));
        }
      },
    },
    h("label", { class: "sr-only", for: id }, "Your email"),
    h("input", { id, type: "email", name: "email", required: true, autocomplete: "email", maxlength: 254, placeholder: "you@example.com" }),
    h("button", { type: "submit", class: "btn" }, "Join the waiting list"),
    msg,
  );
  return form;
}

/** What each system needs: name, system, processor, disk. The Gate's requirements section reads the same rows. */
export const REQUIREMENTS: [string, string, string, string][] = [
  ["macOS", "macOS 13 Ventura or later", "Apple silicon (Intel later)", "About 400 MB free"],
  ["Linux", "Ubuntu 24.04 LTS or later; other apt-based systems need glibc 2.39 or newer", "x86-64", "About 450 MB free"],
  ["Windows", "Windows 10 or later", "x86-64", "About 450 MB free"],
];

/** A piece of an FAQ answer: plain text, a link, or a terminal command (kept off phones). */
export type FaqPart = string | { href: string; text: string } | { code: string };

/**
 * The FAQ as data. On a phone (`phone`), nothing mentions a terminal: no install-command question, no `xattr`, no apt
 * line, since the app isn't installed from there (wave 12).
 */
export function faqItems(phone: boolean, isOn: boolean): [string, FaqPart[]][] {
  return [
    ["Is Banger free?", [freeAnswer(isOn)]],
    ...(isOn ? [["How does paying work?", [balanceAnswer()]] as [string, FaqPart[]]] : []),
    ["Do I need the app to play?", ["No. Every game page has a Play in browser link that opens the game on its own site. The app is where your library, friends and parties live, and Play buttons here open it."]],
    ["Do I need an account?", ["No, you can play without one. Sign in to keep your library and progress across computers, add friends and join parties."]],
    ...(phone
      ? []
      : [
          [
            "Is the install command safe to run?",
            [
              "Piping a script into your shell means trusting it, so ",
              { href: "/install.sh", text: "read it first" },
              ". It only downloads from this site over https, refuses a file whose SHA-256 doesn't match the one published next to it, installs into your Applications folder without sudo, and prints what it did. On Linux the .deb packages need your admin password, so it prints the apt command for you to run rather than running sudo itself.",
            ],
          ] as [string, FaqPart[]],
        ]),
    [
      "My Mac says Banger can't be opened. What now?",
      phone
        ? ["Banger is notarised by Apple, so it opens without a warning. If an older copy still warns, update to the latest from this page."]
        : [
            "Banger is notarised by Apple, so it opens without a warning. If an older copy still warns, update to the latest from this page, or remove the download mark in Terminal: ",
            { code: "xattr -dr com.apple.quarantine /Applications/Banger.app" },
            ".",
          ],
    ],
    [
      "Record says Banger needs Screen Recording, but it's already on. What now?",
      ["This can happen once, after updating from Banger 0.1.4 or earlier on a Mac. In System Settings, Privacy & Security, Screen & System Audio Recording, remove Banger with the minus button (or switch it off and on again). Then press F10 in a game, allow it and reopen the game. Later updates keep the permission."],
    ],
    ["What about phones and Windows?", ["The app runs on macOS and Linux first. Windows follows: join the waiting list above and we'll email you when it's out. Until then, on Windows, play any game in your browser from its page. On a phone, every game plays in your browser."]],
    [
      "How do I uninstall it?",
      phone
        ? ["On a Mac, move Banger from Applications to the Bin. On Linux, remove the banger and banger-runtime packages."]
        : ["On macOS, move Banger from your Applications folder to the Bin. On Linux, run ", { code: "sudo apt remove banger banger-runtime" }, "."],
    ],
  ];
}

/** Whether the page shows the terminal installer: desktops only, never phones or iPads. */
export function showsInstaller(mine: string): boolean {
  return mine !== "mobile";
}

